/**
 * Sprint 24.8 — sub-agent delegation hooks.
 *
 * `useDelegatableAgents` backs the Workforce "available for delegation" indicator;
 * `useDelegationSession` loads a delegation's persisted trace (the sub-agent's
 * task + per-step tool calls + final answer) for the ToolCallCard "view trace"
 * expander. The trace lives in the delegation session row (scope_kind='delegation')
 * — it is deliberately NOT returned to the coordinator's model (Q5), only fetched
 * here for the supervisor's chrome.
 */

import { useQuery } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";

export interface DelegatableAgent {
  id: string;
  name: string;
  role: string | null;
  bio: string | null;
  specialties: string[];
  status: string;
}

export interface DelegationTraceStep {
  text: string;
  toolCalls: Array<{ name: string; args: unknown }>;
}

export interface DelegationTraceMessage {
  role: string;
  content: string;
  trace?: DelegationTraceStep[];
}

function unwrap<T>(r: {
  isError?: boolean;
  structuredContent?: T;
  content?: Array<{ text?: string }>;
}): T {
  if (r.isError) throw new Error(r.content?.[0]?.text ?? "Request failed");
  return r.structuredContent as T;
}

/** Agents the caller can delegate to (visible, non-archived, with live status). */
export function useDelegatableAgents(excludeNodeId?: string | null, enabled = true) {
  return useQuery<DelegatableAgent[]>({
    queryKey: ["delegatable-agents", excludeNodeId ?? ""],
    queryFn: async () => {
      const r = await mcpClient.toolsCall<{ agents: DelegatableAgent[] }>(
        "list_delegatable_agents",
        excludeNodeId ? { exclude_node_id: excludeNodeId } : {},
      );
      return unwrap(r).agents ?? [];
    },
    enabled,
    staleTime: 30_000,
  });
}

/** Load a delegation session's stored trace messages (for "view trace"). */
export function useDelegationSession(conversationId: string | null | undefined) {
  return useQuery<DelegationTraceMessage[]>({
    queryKey: ["delegation-session", conversationId ?? ""],
    queryFn: async () => {
      const r = await mcpClient.toolsCall<{ messages: DelegationTraceMessage[] }>(
        "get_chat_session",
        { session_id: conversationId },
      );
      return unwrap(r).messages ?? [];
    },
    enabled: !!conversationId,
    staleTime: 60_000,
  });
}
